import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Search, Phone, Mail, Calendar as CalendarIcon, MessageSquare, User, Home, Users, Trash2, ChevronRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { useSyncedReload } from "@/hooks/useSyncedReload";

const TYPES = ["note", "call", "email", "meeting", "viewing", "appointment"];
const TYPE_ICONS = { note: MessageSquare, call: Phone, email: Mail, meeting: Users, viewing: Home, appointment: CalendarIcon };

const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const emptyEntry = () => ({
  date_time: nowLocal(),
  entry_type: "note",
  subject: "",
  body: "",
  buyer_id: "", buyer_name: "",
  seller_id: "", seller_name: "",
  property_id: "", property_name: "",
  lead_id: "", lead_name: "",
  follow_up_required: false,
  follow_up_date: "",
});

export default function Diary() {
  const { t, lang } = useLang();
  const [entries, setEntries] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [linkFilter, setLinkFilter] = useState({ type: "all", id: "" });
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const [e, b, s, p, l] = await Promise.all([
        api.get("/diary_entries", { params: search ? { search } : {} }),
        api.get("/buyers"), api.get("/sellers"), api.get("/properties"), api.get("/leads"),
      ]);
      setEntries(e.data);
      setBuyers(b.data); setSellers(s.data); setProperties(p.data); setLeads(l.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useSyncedReload(load);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (typeFilter !== "all" && e.entry_type !== typeFilter) return false;
      if (linkFilter.type !== "all" && linkFilter.id) {
        const key = `${linkFilter.type}_id`;
        if (e[key] !== linkFilter.id) return false;
      }
      return true;
    });
  }, [entries, typeFilter, linkFilter]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((e) => {
      const day = (e.date_time || "").slice(0, 10) || "—";
      if (!groups[day]) groups[day] = [];
      groups[day].push(e);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  const save = async (payload) => {
    try {
      // Resolve link names
      if (payload.buyer_id) payload.buyer_name = buyers.find((b) => b.id === payload.buyer_id)?.name || "";
      if (payload.seller_id) payload.seller_name = sellers.find((s) => s.id === payload.seller_id)?.name || "";
      if (payload.property_id) payload.property_name = properties.find((p) => p.id === payload.property_id)?.name || "";
      if (payload.lead_id) payload.lead_name = leads.find((l) => l.id === payload.lead_id)?.name || "";
      // Convert local datetime-input to ISO
      if (payload.date_time && !payload.date_time.endsWith("Z") && payload.date_time.length === 16) {
        payload.date_time = new Date(payload.date_time).toISOString();
      }
      if (payload.id) await api.put(`/diary_entries/${payload.id}`, payload);
      else await api.post("/diary_entries", payload);
      toast.success(t("saved"));
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/diary_entries/${id}`);
      toast.success(t("deleted"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="diary-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium text-stone-900">{t("diaryTitle")}</h1>
          <p className="text-sm text-stone-500 mt-2">{t("diarySubtitle")}</p>
        </div>
        <Button data-testid="add-diary-button" onClick={() => setDialog(emptyEntry())} className="bg-stone-900 hover:bg-stone-800 text-white">
          <Plus size={16} className="mr-1.5" /> {t("addEntry")}
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input data-testid="diary-search" placeholder={t("diarySearchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-40" data-testid="diary-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allTypes")}</SelectItem>
            {TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t(`diaryType_${tp}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {grouped.length === 0 && (
        <div className="text-center py-16 text-stone-400 border border-dashed border-stone-200 rounded-2xl">
          {t("noDiaryEntries")}
        </div>
      )}

      <div className="space-y-8">
        {grouped.map(([day, items]) => (
          <div key={day}>
            <h3 className="text-xs uppercase tracking-wider text-stone-500 mb-3">{fmtDate(day, lang)}</h3>
            <div className="space-y-2">
              {items.map((e) => {
                const Icon = TYPE_ICONS[e.entry_type] || MessageSquare;
                return (
                  <div key={e.id} data-testid={`diary-row-${e.id}`} className="group bg-white border border-stone-200 rounded-xl p-4 hover:border-stone-400 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
                        <Icon size={16} className="text-stone-700" />
                      </div>
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setDialog({ ...e })}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-stone-500">{(e.date_time || "").slice(11, 16)}</span>
                          <span className="text-[10px] uppercase tracking-wider bg-stone-100 rounded px-1.5 py-0.5 text-stone-600">{t(`diaryType_${e.entry_type}`)}</span>
                          {e.follow_up_required && (
                            <span className="text-[10px] uppercase tracking-wider bg-amber-50 border border-amber-200 text-amber-700 rounded px-1.5 py-0.5">↳ {fmtDate(e.follow_up_date, lang)}</span>
                          )}
                        </div>
                        {e.subject && <p className="text-sm font-medium text-stone-900 mt-1">{e.subject}</p>}
                        {e.body && <p className="text-sm text-stone-600 mt-1 line-clamp-2 whitespace-pre-wrap">{e.body}</p>}
                        <div className="flex flex-wrap gap-1.5 mt-2 text-[11px] text-stone-500">
                          {e.buyer_name && <span className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5"><User size={10} />{e.buyer_name}</span>}
                          {e.seller_name && <span className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5"><Users size={10} />{e.seller_name}</span>}
                          {e.property_name && <span className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5"><Home size={10} />{e.property_name}</span>}
                          {e.lead_name && <span className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5"><ChevronRight size={10} />{e.lead_name}</span>}
                        </div>
                      </div>
                      <button data-testid={`delete-diary-${e.id}`} onClick={() => remove(e.id)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-stone-400 hover:text-red-600 rounded-lg hover:bg-red-50">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <DiaryDialog open={!!dialog} entry={dialog} onClose={() => setDialog(null)} onSave={save}
                   t={t} buyers={buyers} sellers={sellers} properties={properties} leads={leads} />
    </div>
  );
}

function DiaryDialog({ open, entry, onClose, onSave, t, buyers, sellers, properties, leads, refresh }) {
  const [form, setForm] = useState(entry || emptyEntry());
  useEffect(() => { if (entry) setForm(entry); }, [entry]);
  if (!open) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="diary-dialog">
        <DialogHeader>
          <DialogTitle>{form.id ? t("editEntry") : t("addEntry")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("dateTime")}</Label>
              <Input type="datetime-local" data-testid="diary-date-input" value={(form.date_time || "").slice(0, 16)} onChange={(e) => setField("date_time", e.target.value)} />
            </div>
            <div>
              <Label>{t("type")}</Label>
              <Select value={form.entry_type} onValueChange={(v) => setField("entry_type", v)}>
                <SelectTrigger data-testid="diary-type-input"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t(`diaryType_${tp}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>{t("subject")}</Label>
            <Input data-testid="diary-subject-input" value={form.subject || ""} onChange={(e) => setField("subject", e.target.value)} placeholder={t("subjectPlaceholder")} />
          </div>
          <div>
            <Label>{t("notes")}</Label>
            <textarea data-testid="diary-body-input" rows={4} className="w-full text-sm border border-stone-200 rounded-lg p-2" value={form.body || ""} onChange={(e) => setField("body", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <LinkSelect label={t("linkedBuyer")} value={form.buyer_id} options={buyers} onChange={(v) => setField("buyer_id", v)} testId="diary-buyer-select"
              onCreateNew={async (name) => { const { data } = await api.post("/buyers", { name }); await refresh(); setField("buyer_id", data.id); }} />
            <LinkSelect label={t("linkedSeller")} value={form.seller_id} options={sellers} onChange={(v) => setField("seller_id", v)} testId="diary-seller-select"
              onCreateNew={async (name) => { const { data } = await api.post("/sellers", { name }); await refresh(); setField("seller_id", data.id); }} />
            <LinkSelect label={t("linkedProperty")} value={form.property_id} options={properties} onChange={(v) => setField("property_id", v)} testId="diary-property-select"
              onCreateNew={async (name) => { const { data } = await api.post("/properties", { name }); await refresh(); setField("property_id", data.id); }} />
            <LinkSelect label={t("linkedLead")} value={form.lead_id} options={leads} onChange={(v) => setField("lead_id", v)} testId="diary-lead-select"
              onCreateNew={async (name) => { const { data } = await api.post("/leads", { name }); await refresh(); setField("lead_id", data.id); }} />
          </div>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
              <input type="checkbox" data-testid="diary-followup-checkbox" checked={!!form.follow_up_required} onChange={(e) => setField("follow_up_required", e.target.checked)} />
              {t("followUpRequired")}
            </label>
            {form.follow_up_required && (
              <Input type="date" data-testid="diary-followup-date" className="w-40" value={form.follow_up_date || ""} onChange={(e) => setField("follow_up_date", e.target.value)} />
            )}
          </div>
          {form.follow_up_required && (
            <p className="text-[11px] text-stone-400 -mt-2">{t("diaryAutoTaskHint")}</p>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose} data-testid="diary-cancel">{t("cancel")}</Button>
          <Button data-testid="diary-save" onClick={() => onSave(form)} className="bg-stone-900 hover:bg-stone-800 text-white">{t("save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LinkSelect({ label, value, options, onChange, testId, onCreateNew }) {
  const { t } = useLang();
  const handleChange = async (v) => {
    if (v === "none") return onChange("");
    if (v === "__new__" && onCreateNew) {
      const name = window.prompt(t("createNewPromptLabel").replace("{label}", label));
      if (!name || !name.trim()) return;
      try {
        await onCreateNew(name.trim());
        toast.success(t("created"));
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message);
      }
      return;
    }
    onChange(v);
  };
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value || "none"} onValueChange={handleChange}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder={t("none")} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">{t("none")}</SelectItem>
          {onCreateNew && (
            <SelectItem value="__new__" data-testid={`${testId}-create-new`}>
              + {t("createNew").replace("{label}", label.toLowerCase())}
            </SelectItem>
          )}
          {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.ref_code ? `${o.ref_code} · ` : ""}{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
